import { useEffect, useState } from "react";
import { currentPersonSelector, setCurrentPerson } from "@/pages/ContactsPage/slice";
import {
  useDeletePersonAvatarMutation,
  useUpdatePersonAvatarMutation,
  useUploadFileMutation,
} from "@/services/api";
import { getErrorMessage } from "@/services/errors";
import { useAppDispatch, useAppSelector } from "@/services/store";
import { useAlert } from "@/shared/hooks";
import { useRuntimeImage } from "@/entities/runtime-image";
import type { MaybeType } from "@/shared/types";
import type { Person } from "@/services/api";

export const usePersonAvatar = (person?: Person) => {
  const [fileId, setFileId] = useState<MaybeType<string>>(null);

  const currentPersonFromStore = useAppSelector(currentPersonSelector);

  const currentPerson = person ?? currentPersonFromStore;

  const [uploadFile] = useUploadFileMutation();
  const [deletePersonAvatar] = useDeletePersonAvatarMutation();
  const [updatePersonAvatar] = useUpdatePersonAvatarMutation();

  const { getRuntimeImageURL } = useRuntimeImage();

  const dispatch = useAppDispatch();

  const { callError } = useAlert();

  useEffect(() => {
    setFileId(currentPerson?.avatarFile?.fileId ?? null);
  }, [currentPerson]);

  const handleDelete = () => {
    if (!currentPerson?.id || !currentPerson.avatarFile?.fileId) return;
    deletePersonAvatar({ fileId: currentPerson.avatarFile?.fileId, personId: currentPerson.id })
      .unwrap()
      .then((res) => {
        setFileId(res.avatarFile?.fileId ?? null);
        dispatch(setCurrentPerson(res));
      })
      .catch((e) => callError({ text: getErrorMessage(e.code) }));
  };

  const handleUpload = ({ file }: { file: File }) => {
    const formData = new FormData();
    formData.append("file", file);
    uploadFile(formData)
      .unwrap()
      .then((res) => {
        if (!currentPerson?.id) return;
        updatePersonAvatar({ fileId: res.fileId, personId: currentPerson.id })
          .unwrap()
          .then((response) => {
            setFileId(response.avatarFile?.fileId ?? null);
            dispatch(setCurrentPerson(response));
          })
          .catch((e) => callError({ text: getErrorMessage(e.code) }));
      })
      .catch((e) => callError({ text: getErrorMessage(e.code) }));
    return false;
  };

  return {
    handleUpload,
    handleDelete,
    src: getRuntimeImageURL({ fileId: fileId ?? "" }),
  };
};
